/**
 * AI CONTEXT
 * Purpose: project CRUD table page.
 * Key deps: useProjectTableData, TableFiltersRow, AddProjectFormModal.
 * Referenced by: App route ROUTES.PROJECTS.
 * Importance: project catalog drives curriculum/class planning.
 * Refactor: share table+modal state logic via reusable CRUD page hook.
 */
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  getProjectSortValue,
  PROJECT_SORTABLE_FIELDS,
  PROJECT_TABLE_COLUMNS,
} from "#constants/projectOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type ProjectForTable } from "#customTypes/composedTypes/projectTypes"
import { SETTINGS_KEYS, Uuid } from "#customTypes/globalTypes"
import AddProjectFormModal from "#forms/AddProject/AddProjectFormModal"
import useProjectTableData from "#hooks/dataHooks/useProjectTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const ProjectsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<Uuid | null>(null)

  const {
    projectsForTable,
    isReady,
    errors,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useProjectTableData()
  const canSeeProjects = useHasPermission(RESOURCES.PROJECT, USER_ACTIONS.READ)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const columns = useMemo(
    () =>
      buildTableColumns<ProjectForTable>(PROJECT_TABLE_COLUMNS, {
        sortableFields: PROJECT_SORTABLE_FIELDS,
        getSortValue: getProjectSortValue,
        showId: canViewId,
      }),
    [canViewId]
  )

  const handleOpen = () => {
    if (!canSeeProjects) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedProjectId(null)
  }

  const handleRowClick = (selectedProjectId: string) => {
    setSelectedProjectId(selectedProjectId)
    handleOpen()
  }

  if (!canSeeProjects) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Projects</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search by name, medium, skill level, notes, description"
          settingsKey={SETTINGS_KEYS.PROJECTS}
        />

        {isOpen && canSeeProjects && (
          <AddProjectFormModal
            onClose={handleClose}
            projectId={selectedProjectId}
          />
        )}
        <Table2<ProjectForTable>
          data={projectsForTable}
          columns={columns}
          onRowClick={handleRowClick}
          errors={errors}
          isLoading={!isReady}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default ProjectsTable
